/**
 * 数据字典 API
 *
 * @description 提供数据字典类别和选项的管理接口
 */

import { request } from '@umijs/max';

const DICTIONARY_CACHE_TTL_MS = 10 * 60 * 1000;

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const categoriesCache: CacheEntry<DataDictionaryCategory[]> | null = null;
let categoriesCacheRef: CacheEntry<DataDictionaryCategory[]> | null =
  categoriesCache;
let categoriesInFlight: Promise<DataDictionaryCategory[]> | null = null;

const itemsCacheByCategory = new Map<
  string,
  CacheEntry<DataDictionaryItem[]>
>();
const itemsInFlightByCategory = new Map<
  string,
  Promise<DataDictionaryItem[]>
>();

const getNow = () => Date.now();

const isCacheValid = <T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> =>
  Boolean(entry && entry.expiresAt > getNow());

const setCategoriesCache = (value: DataDictionaryCategory[]) => {
  categoriesCacheRef = {
    value,
    expiresAt: getNow() + DICTIONARY_CACHE_TTL_MS,
  };
};

const setItemsCache = (categoryUuid: string, value: DataDictionaryItem[]) => {
  itemsCacheByCategory.set(categoryUuid, {
    value,
    expiresAt: getNow() + DICTIONARY_CACHE_TTL_MS,
  });
};

const fetchCategories = async (): Promise<DataDictionaryCategory[]> => {
  const categories = await request<DataDictionaryCategory[]>(
    '/bff/luole-hunter/data-dictionaries/categories',
  );
  const safeCategories = Array.isArray(categories) ? categories : [];
  setCategoriesCache(safeCategories);
  return safeCategories;
};

const fetchItems = async (
  categoryUuid: string,
): Promise<DataDictionaryItem[]> => {
  const items = await request<DataDictionaryItem[]>(
    `/bff/luole-hunter/data-dictionaries/categories/${categoryUuid}/items`,
  );
  const safeItems = Array.isArray(items) ? items : [];
  setItemsCache(categoryUuid, safeItems);
  return safeItems;
};

const getCategoriesCached = async (options?: {
  forceRefresh?: boolean;
}): Promise<DataDictionaryCategory[]> => {
  const forceRefresh = options?.forceRefresh === true;
  if (!forceRefresh && isCacheValid(categoriesCacheRef)) {
    return categoriesCacheRef.value;
  }

  if (!forceRefresh && categoriesInFlight) {
    return categoriesInFlight;
  }

  categoriesInFlight = fetchCategories().finally(() => {
    categoriesInFlight = null;
  });
  return categoriesInFlight;
};

const getItemsCached = async (
  categoryUuid: string,
  options?: { forceRefresh?: boolean },
): Promise<DataDictionaryItem[]> => {
  const forceRefresh = options?.forceRefresh === true;
  const cacheEntry = itemsCacheByCategory.get(categoryUuid);
  if (!forceRefresh && cacheEntry && cacheEntry.expiresAt > getNow()) {
    return cacheEntry.value;
  }

  const inFlight = itemsInFlightByCategory.get(categoryUuid);
  if (!forceRefresh && inFlight) {
    return inFlight;
  }

  const promise = fetchItems(categoryUuid).finally(() => {
    itemsInFlightByCategory.delete(categoryUuid);
  });
  itemsInFlightByCategory.set(categoryUuid, promise);
  return promise;
};

const getItemsByCodeCached = async (
  code: string,
  options?: { forceRefresh?: boolean },
): Promise<DataDictionaryItem[]> => {
  const categories = await getCategoriesCached(options);
  const category = categories.find(
    (item) => item.code === code && item.isActive !== false,
  );
  if (!category) {
    return [];
  }
  return getItemsCached(category.uuid, options);
};

const clearDictionaryCache = () => {
  categoriesCacheRef = null;
  categoriesInFlight = null;
  itemsCacheByCategory.clear();
  itemsInFlightByCategory.clear();
};

/**
 * 数据字典类别
 */
export interface DataDictionaryCategory {
  /** 数字 ID */
  id?: number;
  /** 唯一标识 */
  uuid: string;
  /** 名称 */
  name: string;
  /** 编码 */
  code: string;
  /** 描述 */
  description?: string;
  /** 父级 UUID */
  parentUuid?: string | null;
  /** 排序值 */
  sortOrder?: number;
  /** 是否启用 */
  isActive?: boolean;
  /** 选项数量 */
  itemCount?: number;
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
}

/**
 * 数据字典选项
 */
export interface DataDictionaryItem {
  /** 数字 ID */
  id?: number;
  /** 唯一标识 */
  uuid: string;
  /** 所属类别 UUID */
  categoryUuid: string;
  /** 父级选项 UUID，支持树形结构 */
  parentUuid?: string | null;
  /** 显示名称 */
  label: string;
  /** 选项值 */
  value: string;
  /** 描述 */
  description?: string;
  /** 排序值 */
  sortOrder?: number;
  /** 是否启用 */
  isActive?: boolean;
  /** 是否默认 */
  isDefault?: boolean;
  /** 扩展字段 */
  extra?: Record<string, unknown>;
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
}

/**
 * 类别保存入参
 */
export interface DataDictionaryCategoryPayload {
  /** 名称 */
  name: string;
  /** 编码 */
  code: string;
  /** 描述 */
  description?: string;
  /** 父级 UUID */
  parentUuid?: string;
  /** 排序值 */
  sortOrder?: number;
  /** 是否启用 */
  isActive?: boolean;
}

/**
 * 选项保存入参
 */
export interface DataDictionaryItemPayload {
  /** 所属类别 UUID */
  categoryUuid?: string;
  /** 父级选项 UUID，支持树形结构 */
  parentUuid?: string;
  /** 显示名称 */
  label: string;
  /** 选项值 */
  value: string;
  /** 描述 */
  description?: string;
  /** 排序值 */
  sortOrder?: number;
  /** 是否启用 */
  isActive?: boolean;
  /** 是否默认 */
  isDefault?: boolean;
  /** 扩展字段 */
  extra?: Record<string, unknown>;
}

/**
 * 数据字典 API
 */
export const dataDictionaryApi = {
  /**
   * 获取全部类别
   */
  getCategories: () => getCategoriesCached(),

  getCategoriesRaw: () =>
    request<DataDictionaryCategory[]>(
      '/bff/luole-hunter/data-dictionaries/categories',
    ),

  /**
   * 创建类别
   *
   * @param payload - 类别信息
   */
  createCategory: (payload: DataDictionaryCategoryPayload) => {
    clearDictionaryCache();
    return request<DataDictionaryCategory>(
      '/bff/luole-hunter/data-dictionaries/categories',
      { params: payload },
    );
  },

  /**
   * 更新类别
   *
   * @param uuid - 类别 UUID
   * @param payload - 更新内容
   */
  updateCategory: (
    uuid: string,
    payload: Partial<DataDictionaryCategoryPayload>,
  ) => {
    clearDictionaryCache();
    return request<DataDictionaryCategory>(
      `/bff/luole-hunter/data-dictionaries/categories/${uuid}`,
      payload,
    );
  },

  /**
   * 删除类别
   *
   * @param uuid - 类别 UUID
   */
  deleteCategory: (uuid: string) => {
    clearDictionaryCache();
    return request<DataDictionaryCategory>(
      `/bff/luole-hunter/data-dictionaries/categories/${uuid}`,
    );
  },

  /**
   * 获取选项列表
   *
   * @param categoryUuid - 类别 UUID
   */
  getItems: (categoryUuid: string) => getItemsCached(categoryUuid),

  getItemsRaw: (categoryUuid: string) =>
    request<DataDictionaryItem[]>(
      `/bff/luole-hunter/data-dictionaries/categories/${categoryUuid}/items`,
    ),

  getItemsByCode: (code: string) => getItemsByCodeCached(code),

  preloadByCodes: async (codes: string[]) => {
    if (!Array.isArray(codes) || codes.length === 0) {
      return;
    }
    await Promise.allSettled(codes.map((code) => getItemsByCodeCached(code)));
  },

  refreshByCodes: async (codes: string[]) => {
    if (!Array.isArray(codes) || codes.length === 0) {
      return;
    }
    await Promise.allSettled(
      codes.map((code) => getItemsByCodeCached(code, { forceRefresh: true })),
    );
  },

  clearCache: () => {
    clearDictionaryCache();
  },

  /**
   * 创建选项
   *
   * @param categoryUuid - 类别 UUID
   * @param payload - 选项数据
   */
  createItem: (categoryUuid: string, payload: DataDictionaryItemPayload) => {
    clearDictionaryCache();
    return request<DataDictionaryItem>(
      `/bff/luole-hunter/data-dictionaries/categories/${categoryUuid}/items`,
      payload,
    );
  },

  /**
   * 更新选项
   *
   * @param uuid - 选项 UUID
   * @param payload - 更新内容
   */
  updateItem: (uuid: string, payload: Partial<DataDictionaryItemPayload>) => {
    clearDictionaryCache();
    return request<DataDictionaryItem>(
      `/bff/luole-hunter/data-dictionaries/items/${uuid}`,
      payload,
    );
  },

  /**
   * 删除选项
   *
   * @param uuid - 选项 UUID
   */
  deleteItem: (uuid: string) => {
    clearDictionaryCache();
    return request<DataDictionaryCategory>(
      `/bff/luole-hunter/data-dictionaries/items/${uuid}`,
    );
  },
};
